import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  ORDER_STATUS, accentColorForType,
} from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Paper, Grid, Chip, Button, Avatar
} from '@mui/material';
import moment from 'moment/min/moment-with-locales';
import { FONT_FAMILY, MAIN_COLOR } from '../common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';

export default function PartnerDashboard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((s) => s.auth);
  const partnerdata = useSelector((s) => s.partnerdata);
  const partnerorderdata = useSelector((s) => s.partnerorderdata);
  const { fetchPartnersByOwner, fetchPartnerOrdersByPartner } = api;

  useEffect(() => {
    if (auth?.profile?.uid) {
      dispatch(fetchPartnersByOwner(auth.profile.uid));
    }
  }, [dispatch, fetchPartnersByOwner, auth?.profile?.uid]);

  const myPartner = (partnerdata.partners || [])[0];

  useEffect(() => {
    if (myPartner?.id) {
      dispatch(fetchPartnerOrdersByPartner(myPartner.id));
    }
  }, [dispatch, fetchPartnerOrdersByPartner, myPartner?.id]);

  const orders = partnerorderdata.orders || [];
  const stats = useMemo(() => {
    const today = moment().startOf('day').valueOf();
    const week = moment().subtract(7, 'days').valueOf();
    const pending = orders.filter((o) => o.status === ORDER_STATUS.PENDING).length;
    const todayCount = orders.filter((o) => o.createdAt >= today).length;
    const weekRevenue = orders
      .filter((o) => o.status === ORDER_STATUS.COMPLETED && o.createdAt >= week)
      .reduce((acc, o) => acc + Number(o.total || 0), 0);
    return { pending, todayCount, weekRevenue };
  }, [orders]);

  if (!auth?.profile?.uid) return <CircularLoading />;
  if (!myPartner) {
    return (
      <Box sx={{ p: 4, fontFamily: FONT_FAMILY, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('no_partner_yet') || 'Aún no tienes negocio'}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t('no_partner_yet_subtitle') || 'Crea tu negocio para empezar a recibir reservas y pedidos.'}
        </Typography>
        <Button variant="contained" sx={{ backgroundColor: MAIN_COLOR }} onClick={() => navigate('/partner/business')}>
          {t('create_business') || 'Crear mi negocio'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar variant="rounded" src={myPartner.images?.cover || undefined} sx={{ width: 64, height: 64 }}>
          {(myPartner.businessName || '?').slice(0, 1)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{myPartner.businessName}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small" label={myPartner.partnerType}
              sx={{
                backgroundColor: `${accentColorForType(myPartner.partnerType)}1a`,
                color: accentColorForType(myPartner.partnerType),
                fontWeight: 600,
              }}
            />
            {myPartner.approved ? (
              <Chip size="small" label={t('approved') || 'Aprobado'} color="success" />
            ) : (
              <Chip size="small" label={t('pending_approval') || 'Pendiente de aprobación'} color="warning" />
            )}
          </Stack>
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('pending_orders') || 'Pendientes', value: stats.pending },
          { label: t('orders_today') || 'Hoy', value: stats.todayCount },
          { label: t('revenue_7d') || 'Ingresos 7d', value: `${myPartner.currency || ''} ${stats.weekRevenue.toFixed(2)}` },
        ].map((s) => (
          <Grid key={s.label} item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/partner/business')}>
          {t('partner_business') || 'Mi negocio'}
        </Button>
        <Button variant="outlined" onClick={() => navigate('/partner/inventory')}>
          {t('partner_inventory') || 'Inventario'}
        </Button>
        <Button variant="contained" sx={{ backgroundColor: MAIN_COLOR }} onClick={() => navigate('/partner/orders')}>
          {t('partner_orders_inbox') || 'Bandeja de pedidos'}
        </Button>
      </Stack>

      <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>
        {t('latest_orders') || 'Últimos pedidos'}
      </Typography>
      <Stack spacing={1.25}>
        {orders.slice(0, 8).map((o) => (
          <Paper
            key={o.id} variant="outlined"
            sx={{ p: 1.5, borderRadius: 3, cursor: 'pointer' }}
            onClick={() => navigate(`/myorders/${o.id}`)}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{o.customerName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  #{o.reference} · {moment(o.createdAt).format('lll')}
                </Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5}>
                <Chip size="small" label={o.status} />
                <Typography sx={{ fontWeight: 700 }}>
                  {o.currency} {Number(o.total || 0).toFixed(2)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        ))}
        {orders.length === 0 && (
          <Typography color="text.secondary">{t('no_orders_yet') || 'Aún no hay pedidos.'}</Typography>
        )}
      </Stack>
    </Box>
  );
}
